import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Layers3, Loader2, MessageCircleQuestion, Search, Sparkles, Tag, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import {
  listQuestionBrowser,
  type QuestionBrowserFacet,
  type QuestionBrowserItem,
  type QuestionBrowserResponse,
} from '../services/questionService';

interface QuestionExplorerManagerProps {
  isOpen: boolean;
  onClose: () => void;
  token: string;
  isDarkMode: boolean;
}

const DEFAULT_SHOW_SLUG = 'mlst';

function formatTimestampParam(timestampSeconds: number | null): string {
  if (timestampSeconds === null || !Number.isFinite(timestampSeconds)) {
    return '';
  }

  const totalSeconds = Math.max(0, Math.floor(timestampSeconds));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h${minutes}m${seconds}s`;
  }

  return `${minutes}m${seconds}s`;
}

function mergeBrowserResponses(
  previous: QuestionBrowserResponse | null,
  next: QuestionBrowserResponse,
  append: boolean
): QuestionBrowserResponse {
  if (!append || !previous) {
    return next;
  }

  const deduped = new Map<string, QuestionBrowserItem>();
  [...previous.questions, ...next.questions].forEach((question) => {
    deduped.set(question.id, question);
  });

  return {
    ...next,
    questions: [...deduped.values()],
    count: [...deduped.values()].length,
  };
}

function groupQuestionsByTopic(questions: QuestionBrowserItem[]) {
  const groups = new Map<string, QuestionBrowserItem[]>();
  for (const question of questions) {
    const topic = (question.topic || 'Uncategorized').trim() || 'Uncategorized';
    const bucket = groups.get(topic);
    if (bucket) {
      bucket.push(question);
    } else {
      groups.set(topic, [question]);
    }
  }

  return [...groups.entries()]
    .map(([topic, items]) => ({ topic, items }))
    .sort((left, right) => {
      if (right.items.length !== left.items.length) {
        return right.items.length - left.items.length;
      }
      return left.topic.localeCompare(right.topic);
    });
}

const QuestionExplorerManager: React.FC<QuestionExplorerManagerProps> = ({ isOpen, onClose, token, isDarkMode }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [showFilter, setShowFilter] = useState('all');
  const [personFilter, setPersonFilter] = useState('all');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [publishedOnly, setPublishedOnly] = useState(true);
  const [requireYoutube, setRequireYoutube] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [browserData, setBrowserData] = useState<QuestionBrowserResponse | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const lastRequestedOffsetRef = useRef(-1);

  useEffect(() => {
    if (!isOpen) return;
    const timeoutId = window.setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => window.clearTimeout(timeoutId);
  }, [isOpen, query]);

  const discoveryMode = debouncedQuery.length === 0 && !selectedTopic;

  const loadQuestions = useCallback(async ({ append = false, offset = 0 }: { append?: boolean; offset?: number } = {}) => {
    if (!token || !isOpen) return;
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const response = await listQuestionBrowser({
        query: debouncedQuery,
        show: showFilter,
        person: personFilter === 'all' ? undefined : personFilter,
        topic: debouncedQuery ? undefined : (selectedTopic || undefined),
        discovery: discoveryMode,
        publishedOnly,
        requireYoutube,
        limit: discoveryMode ? 1 : 40,
        offset,
      }, token);
      setBrowserData((previous) => mergeBrowserResponses(previous, response, append));
    } catch (err: any) {
      setError(err.message || 'Failed to load question explorer');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [debouncedQuery, discoveryMode, isOpen, personFilter, publishedOnly, requireYoutube, selectedTopic, showFilter, token]);

  useEffect(() => {
    if (!isOpen) return;
    lastRequestedOffsetRef.current = -1;
    setBrowserData(null);
    void loadQuestions({ append: false, offset: 0 });
  }, [debouncedQuery, isOpen, loadQuestions, personFilter, publishedOnly, requireYoutube, selectedTopic, showFilter]);

  useEffect(() => {
    if (debouncedQuery.trim().length > 0 && selectedTopic) {
      setSelectedTopic('');
    }
  }, [debouncedQuery, selectedTopic]);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setDebouncedQuery('');
      setShowFilter('all');
      setPersonFilter('all');
      setSelectedTopic('');
      setPublishedOnly(true);
      setRequireYoutube(true);
      setError(null);
      setBrowserData(null);
    }
  }, [isOpen]);

  const shows = useMemo(() => browserData?.shows || [], [browserData]);
  const people = useMemo(() => browserData?.people || [], [browserData]);
  const topics = useMemo(() => browserData?.topics || [], [browserData]);
  const questions = useMemo(() => browserData?.questions || [], [browserData]);
  const topicGroups = useMemo(() => groupQuestionsByTopic(questions), [questions]);
  const hasMore = Boolean(browserData?.pagination?.hasMore);

  const mutedTextClassName = isDarkMode ? 'text-[#A9B4C3]' : 'text-[#5B687C]';
  const panelClassName = isDarkMode
    ? 'border-[#344052] bg-[#162131]/96 text-[#F4F4F4]'
    : 'border-[#D6DCE4] bg-white/[0.97] text-[#232E3F]';
  const cardClassName = isDarkMode
    ? 'border-[#344052] bg-[#101925]/90'
    : 'border-[#D6DCE4] bg-white/90';
  const inputClassName = isDarkMode
    ? 'border-white/[0.12] bg-white/[0.08] text-[#F4F4F4] placeholder:text-[#94A0B2]'
    : 'border-[#D6DCE4] bg-white text-[#232E3F] placeholder:text-[#7B8798]';

  useEffect(() => {
    if (!isOpen || discoveryMode || !hasMore || loading || loadingMore || !loadMoreRef.current) {
      return;
    }

    const node = loadMoreRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting) {
          return;
        }

        const nextOffset = questions.length;
        if (lastRequestedOffsetRef.current === nextOffset) {
          return;
        }

        lastRequestedOffsetRef.current = nextOffset;
        void loadQuestions({ append: true, offset: nextOffset });
      },
      {
        rootMargin: '420px 0px',
      }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [discoveryMode, hasMore, isOpen, loadQuestions, loading, loadingMore, questions.length]);

  const openSessionAtQuestion = (question: QuestionBrowserItem) => {
    const suffix = question.timestampSeconds != null
      ? `?t=${formatTimestampParam(question.timestampSeconds)}`
      : '';
    onClose();
    navigate(`/session/${question.episodeId}${suffix}`);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[170] flex items-stretch justify-center bg-[#111826]/[0.78] backdrop-blur-sm">
      <div className={`relative flex h-full w-full flex-col overflow-hidden border-l shadow-[0_40px_90px_-40px_rgba(10,14,20,0.92)] ${panelClassName}`}>
        <div className={`sticky top-0 z-20 border-b px-6 py-5 backdrop-blur-2xl ${isDarkMode ? 'border-white/[0.08] bg-[#162131]/88' : 'border-[#D6DCE4] bg-[#F4F4F4]/88'}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className={`font-headlines text-[10px] font-semibold uppercase tracking-[0.28em] ${isDarkMode ? 'text-[#FFCC66]' : 'text-[#A36A00]'}`}>
                Question Explorer
              </p>
              <h2 className="mt-2 font-headlines text-2xl font-bold tracking-tight">Cross-session questions</h2>
              <p className={`mt-2 max-w-3xl font-body text-sm leading-6 ${mutedTextClassName}`}>
                Topic-grouped question browsing across the archive. Open a question at the exact point it was asked, then refine the question bank from the session itself.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className={`rounded-full p-2 transition-colors ${isDarkMode ? 'text-gray-400 hover:bg-white/[0.08]' : 'text-gray-600 hover:bg-[#E8ECF1]'}`}
            >
              <X size={18} />
            </button>
          </div>

          <div className="mt-5 grid gap-3 xl:grid-cols-[minmax(0,1.45fr)_180px_240px_auto_auto_auto]">
            <div className="relative">
              <Search className={`pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 ${mutedTextClassName}`} />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search questions, answers, askers, sessions, topics"
                className={`w-full rounded-2xl border py-3 pl-11 pr-4 font-body text-sm ${inputClassName}`}
              />
            </div>
            <select
              value={showFilter}
              onChange={(event) => setShowFilter(event.target.value)}
              className={`rounded-2xl border px-4 py-3 font-body text-sm ${inputClassName}`}
            >
              <option value="all">all</option>
              {shows.map((show: QuestionBrowserFacet) => (
                <option key={show.slug} value={show.slug}>
                  {show.slug} ({show.count})
                </option>
              ))}
            </select>
            <select
              value={personFilter}
              onChange={(event) => setPersonFilter(event.target.value)}
              className={`rounded-2xl border px-4 py-3 font-body text-sm ${inputClassName}`}
            >
              <option value="all">all people</option>
              {people.map((person: QuestionBrowserFacet) => (
                <option key={person.name} value={person.name}>
                  {person.name} ({person.count})
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setPublishedOnly((current) => !current)}
              className={`rounded-full border px-4 py-2 font-headlines text-sm font-semibold ${
                publishedOnly
                  ? (isDarkMode ? 'border-[#FFCC66]/35 bg-[#FFB52B]/12 text-[#FFE3A1]' : 'border-[#F2D9A6] bg-[#FFF6DB] text-[#A36A00]')
                  : inputClassName
              }`}
            >
              Published only
            </button>
            <button
              type="button"
              onClick={() => setRequireYoutube((current) => !current)}
              className={`rounded-full border px-4 py-2 font-headlines text-sm font-semibold ${
                requireYoutube
                  ? (isDarkMode ? 'border-[#BCD7FF]/35 bg-[#355E9A]/18 text-[#D8E8FF]' : 'border-[#BFD3F0] bg-[#EEF3FF] text-[#355E9A]')
                  : inputClassName
              }`}
            >
              YouTube only
            </button>
            <button
              type="button"
              onClick={() => void loadQuestions({ append: false, offset: 0 })}
              className={`rounded-full border px-4 py-2 font-headlines text-sm font-semibold ${inputClassName}`}
            >
              Refresh
            </button>
          </div>

          <div className={`mt-4 flex flex-wrap items-center gap-3 text-xs ${mutedTextClassName}`}>
            {discoveryMode ? (
              <>
                <span>{topics.length} topic{topics.length === 1 ? '' : 's'} ready to explore</span>
                <span>Pick a topic or start typing to switch into search mode.</span>
              </>
            ) : (
              <>
                {questions.length === 0 ? (
                  <span>No questions found for this search.</span>
                ) : (
                  <>
                    <span>Showing {questions.length}{hasMore ? '+' : ''} question{questions.length === 1 ? '' : 's'}</span>
                    {hasMore ? <span>Scroll down to load more.</span> : <span>Grouped by topic.</span>}
                  </>
                )}
              </>
            )}
            {selectedTopic ? (
              <button
                type="button"
                onClick={() => setSelectedTopic('')}
                className={`rounded-full border px-3 py-1 font-headlines text-[11px] font-semibold uppercase tracking-[0.16em] ${
                  isDarkMode
                    ? 'border-[#FFCC66]/35 bg-[#5B3A00]/72 text-[#FFF2C7] shadow-[0_16px_30px_-22px_rgba(0,0,0,0.55)]'
                    : 'border-[#F2D9A6] bg-[#FFF6DB] text-[#A36A00]'
                }`}
              >
                Topic: {selectedTopic}
              </button>
            ) : null}
          </div>
        </div>

        <div className={`flex-1 overflow-y-auto px-6 pb-10 pt-6 ${isDarkMode ? 'bg-[#101925]' : 'bg-[#F4F4F4]'}`}>
          {loading ? (
            <div className={`flex min-h-[45vh] items-center justify-center font-body text-sm ${mutedTextClassName}`}>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading questions…
            </div>
          ) : error ? (
            <div className={`rounded-[24px] border px-6 py-8 text-center shadow-[0_20px_40px_-28px_rgba(35,46,63,0.25)] ${panelClassName}`}>
              <div className="font-headlines text-lg font-semibold">Question explorer failed to load</div>
              <p className={`mt-2 font-body text-sm ${mutedTextClassName}`}>{error}</p>
            </div>
          ) : discoveryMode ? (
            <div className="space-y-6">
              <div className={`rounded-[24px] border px-6 py-8 shadow-[0_20px_40px_-28px_rgba(35,46,63,0.25)] ${panelClassName}`}>
                <div className="font-headlines text-lg font-semibold">Explore by topic</div>
                <p className={`mt-2 font-body text-sm ${mutedTextClassName}`}>
                  Start with a topic, then open the exact session moment and refine question records from there.
                </p>
              </div>
              {topics.length === 0 ? (
                <div className={`rounded-[24px] border px-6 py-8 text-center shadow-[0_20px_40px_-28px_rgba(35,46,63,0.25)] ${panelClassName}`}>
                  <Tag className="mx-auto h-8 w-8 text-brand-yellow" />
                  <div className="mt-3 font-headlines text-lg font-semibold">No topics matched this scope</div>
                  <p className={`mt-2 font-body text-sm ${mutedTextClassName}`}>
                    Try a different show/person scope or start with a direct search.
                  </p>
                </div>
              ) : (
                <div className="grid gap-4 xl:grid-cols-3">
                  {topics.map((topic) => (
                    <button
                      key={topic.name}
                      type="button"
                      onClick={() => setSelectedTopic(topic.name || '')}
                      className={`rounded-[24px] border p-5 text-left shadow-[0_20px_40px_-28px_rgba(35,46,63,0.2)] transition duration-300 hover:-translate-y-0.5 hover:shadow-[0_26px_50px_-30px_rgba(35,46,63,0.28)] ${cardClassName}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-headlines text-xl font-bold tracking-tight">{topic.name}</p>
                          <p className={`mt-2 text-xs uppercase tracking-[0.18em] ${mutedTextClassName}`}>
                            {topic.count} question{topic.count === 1 ? '' : 's'}
                          </p>
                        </div>
                        <span className={`rounded-full px-3 py-1 font-headlines text-[11px] font-semibold uppercase tracking-[0.14em] ${isDarkMode ? 'border border-[#BCD7FF]/18 bg-[#213554]/88 text-[#F4F8FF]' : 'bg-[#EEF3FF] text-[#355E9A]'}`}>
                          Topic
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : topicGroups.length === 0 ? (
            <div className={`rounded-[24px] border px-6 py-8 text-center shadow-[0_20px_40px_-28px_rgba(35,46,63,0.25)] ${panelClassName}`}>
              <MessageCircleQuestion className="mx-auto h-8 w-8 text-brand-yellow" />
              <div className="mt-3 font-headlines text-lg font-semibold">No questions matched</div>
              <p className={`mt-2 font-body text-sm ${mutedTextClassName}`}>
                Try a broader search or relax one of the published / YouTube filters.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {topicGroups.map((group) => (
                <section key={group.topic} className={`rounded-[24px] border shadow-[0_20px_40px_-28px_rgba(35,46,63,0.32)] ${cardClassName}`}>
                  <div className={`border-b px-5 py-4 ${isDarkMode ? 'border-white/[0.08]' : 'border-[#E3E7EC]'}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="font-headlines text-xl font-bold tracking-tight">{group.topic}</p>
                        <p className={`mt-1 text-xs uppercase tracking-[0.22em] ${mutedTextClassName}`}>
                          {group.items.length} question{group.items.length === 1 ? '' : 's'}
                        </p>
                      </div>
                      <div className={`rounded-full px-3 py-1 font-headlines text-[11px] font-semibold uppercase tracking-[0.14em] ${isDarkMode ? 'border border-[#BCD7FF]/18 bg-[#213554]/88 text-[#F4F8FF]' : 'bg-[#EEF3FF] text-[#355E9A]'}`}>
                        Topic group
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-4 p-5 xl:grid-cols-2">
                    {group.items.map((question) => {
                      const showChipVisible = showFilter === 'all' && question.showSlug && question.showSlug !== DEFAULT_SHOW_SLUG;
                      return (
                        <article
                          key={question.id}
                          className={`rounded-[24px] border p-5 shadow-[0_20px_40px_-28px_rgba(35,46,63,0.2)] ${isDarkMode ? 'border-white/[0.08] bg-white/[0.04]' : 'border-[#E3E7EC] bg-white/92'}`}
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`rounded-full px-2.5 py-1 font-headlines text-[11px] font-semibold uppercase tracking-[0.12em] ${
                              question.questionType === 'implicit'
                                ? (isDarkMode ? 'bg-[#26344A] text-[#BCD7FF]' : 'bg-[#EEF3FF] text-[#355E9A]')
                                : (isDarkMode ? 'bg-[#153526] text-[#A9E2C4]' : 'bg-[#EAF7EF] text-[#2D7250]')
                            }`}>
                              {question.questionType}
                            </span>
                            {question.askedBy ? (
                              <span className={`rounded-full px-2.5 py-1 font-headlines text-[11px] font-semibold uppercase tracking-[0.12em] ${isDarkMode ? 'bg-white/[0.08] text-[#D7DDE5]' : 'bg-[#E8ECF1] text-[#566377]'}`}>
                                {question.askedBy}
                              </span>
                            ) : null}
                            {showChipVisible ? (
                              <span className={`rounded-full px-2.5 py-1 font-headlines text-[11px] font-semibold uppercase tracking-[0.12em] ${isDarkMode ? 'bg-[#2D2446] text-[#D9D2FF]' : 'bg-[#F1EFFA] text-[#5E548E]'}`}>
                                {question.showSlug}
                              </span>
                            ) : null}
                            {question.familyTitle ? (
                              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-headlines text-[11px] font-semibold uppercase tracking-[0.12em] ${isDarkMode ? 'bg-[#FFB52B]/12 text-[#FFE3A1]' : 'bg-[#FFF4D9] text-[#8A6100]'}`}>
                                <Layers3 className="h-3 w-3" />
                                {question.familyTitle}
                              </span>
                            ) : null}
                          </div>

                          <h3 className="mt-4 font-headlines text-xl font-bold tracking-tight">{question.questionText}</h3>
                          <p className={`mt-2 font-headlines text-sm font-semibold uppercase tracking-[0.18em] ${mutedTextClassName}`}>
                            {question.sessionTitle}
                          </p>

                          {question.answerSummary ? (
                            <p className={`mt-4 line-clamp-4 font-body text-sm leading-6 ${mutedTextClassName}`}>
                              {question.answerSummary}
                            </p>
                          ) : (
                            <p className={`mt-4 font-body text-sm leading-6 ${mutedTextClassName}`}>
                              No answer summary is stored yet for this question.
                            </p>
                          )}

                          <div className={`mt-5 flex flex-wrap items-center gap-3 text-xs ${mutedTextClassName}`}>
                            {question.timecode ? <span>{question.timecode}</span> : <span>No timestamp</span>}
                            {question.familyVersionNumber != null ? <span>v{question.familyVersionNumber}</span> : null}
                            {question.publishedAt ? <span>{new Date(question.publishedAt).toLocaleDateString()}</span> : null}
                          </div>

                          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                            <div className={`text-xs ${mutedTextClassName}`}>
                              {showFilter === 'all' ? 'Multi-show browsing' : `Scoped to ${showFilter}`}
                            </div>
                            <button
                              type="button"
                              onClick={() => openSessionAtQuestion(question)}
                              className="inline-flex items-center gap-2 rounded-full bg-[#FFB52B] px-4 py-2 font-headlines text-sm font-semibold text-[#232E3F] shadow-[0_16px_32px_-22px_rgba(255,181,43,0.88)] transition hover:-translate-y-0.5 hover:bg-[#FFC54D]"
                            >
                              <Sparkles className="h-4 w-4" />
                              Open at question
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </section>
              ))}

              {hasMore ? (
                <div ref={loadMoreRef} className="flex justify-center py-2">
                  <div className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm ${mutedTextClassName}`}>
                    <Loader2 className={`h-4 w-4 ${loadingMore ? 'animate-spin' : ''}`} />
                    Loading more questions as you scroll
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionExplorerManager;
